import { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import {
  ChevronRight, Star, Save, Loader2, CreditCard,
  CheckCircle2, XCircle, AlertTriangle, Clock, ExternalLink, RefreshCw,
  Pencil, X, HelpCircle, Info,
} from 'lucide-react';
import { useUserProfile } from '../../context/UserProfileContext';
import { PaymentMethodLogo, type PaymentMethodKey } from '../../components/ui/PaymentMethodLogo';
import {
  usePaymentConfig, OrgPaymentConfig, OrgPaymentMethodConfig, OfflinePaymentMethodType,
  usePaymentScheduleDefaults, PaymentScheduleDefaults,
} from '../../hooks/usePaymentConfig';
import { useOrgSettings } from '../../hooks/useOrgSettings';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { CalculateTaxesCard } from '../../components/ui/CalculateTaxesCard';
import { api } from '../../lib/api';
import type { StripeConnectStatus } from '../../types/admin';
import { IS_FIREBASE_CONFIGURED } from '../../config/firebase';

const isDevMode = !IS_FIREBASE_CONFIGURED;

// ── Method definitions ──────────────────────────────────────────────────────

interface MethodDef {
  type: OfflinePaymentMethodType;
  label: string;
  icon: string;
  color: string;
  identifierLabel?: string;
  identifierPlaceholder?: string;
  hasIdentifier: boolean;
  hasCustomLabel?: boolean;
  hasCustomInstructions?: boolean;
}

const METHOD_DEFS: MethodDef[] = [
  { type: 'venmo', label: 'Venmo', icon: 'V', color: 'bg-[#3D95CE]', identifierLabel: 'Venmo handle', identifierPlaceholder: '@your-handle', hasIdentifier: true },
  { type: 'cashapp', label: 'Cash App', icon: '$', color: 'bg-[#00D632]', identifierLabel: '$cashtag', identifierPlaceholder: '$yourtag', hasIdentifier: true },
  { type: 'paypal', label: 'PayPal', icon: 'P', color: 'bg-[#003087]', identifierLabel: 'PayPal.me handle', identifierPlaceholder: 'yourhandle', hasIdentifier: true },
  { type: 'zelle', label: 'Zelle', icon: 'Z', color: 'bg-[#6D1ED4]', identifierLabel: 'Phone or email', identifierPlaceholder: 'email@example.com', hasIdentifier: true },
  { type: 'check', label: 'Check', icon: 'Ch', color: 'bg-slate', identifierLabel: 'Payable to', identifierPlaceholder: 'Company Name LLC', hasIdentifier: true },
  { type: 'cash', label: 'Cash', icon: 'Ca', color: 'bg-emerald-600', hasIdentifier: false },
  { type: 'custom', label: 'Custom', icon: '?', color: 'bg-midnight', hasIdentifier: true, hasCustomLabel: true, hasCustomInstructions: true, identifierLabel: 'Instructions', identifierPlaceholder: 'Payment instructions for customers' },
];

const HELP_METHODS: OfflinePaymentMethodType[] = ['venmo', 'cashapp', 'paypal'];

function getDefaultMethods(): OrgPaymentMethodConfig[] {
  return METHOD_DEFS.map(def => ({
    type: def.type,
    enabled: false,
    label: def.label,
    identifier: '',
  }));
}

// ── "Where do I find this?" help content ────────────────────────────────────

const HELP_CONTENT: Record<string, { title: string; steps: string[] }> = {
  venmo: {
    title: 'How to find your Venmo handle',
    steps: [
      'Open the Venmo app on your phone.',
      'Tap the "Me" tab at the bottom right.',
      'Your handle is shown below your name, starting with "@" (e.g. @YourName).',
      'You can also find it under Settings → Profile.',
    ],
  },
  cashapp: {
    title: 'How to find your Cash App $cashtag',
    steps: [
      'Open the Cash App on your phone.',
      'Tap the profile icon in the top-right corner.',
      'Your $cashtag is displayed near the top of the screen (e.g. $YourTag).',
      'You can edit it by tapping on it if needed.',
    ],
  },
  paypal: {
    title: 'How to find your PayPal.me handle',
    steps: [
      'Log in to PayPal on the web or app.',
      'Go to paypal.me in your browser while logged in.',
      'If you already have a PayPal.me link, it will show your handle (e.g. paypal.me/YourHandle).',
      'If you don\'t have one yet, you can create it from that same page.',
    ],
  },
};

function HelpModal({ type, onClose }: { type: OfflinePaymentMethodType; onClose: () => void }) {
  const content = HELP_CONTENT[type];
  if (!content) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4">
      <div className="absolute inset-0 bg-midnight/50" onClick={onClose} aria-hidden="true" />
      <div className="relative w-full max-w-sm bg-white rounded-t-2xl sm:rounded-2xl shadow-brand-lg p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-[16px] font-semibold text-midnight">{content.title}</h3>
          <button type="button" onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-slate hover:text-midnight hover:bg-mist transition-colors">
            <X size={16} />
          </button>
        </div>
        <ol className="flex flex-col gap-3">
          {content.steps.map((step, i) => (
            <li key={i} className="flex gap-3 text-[13px] text-midnight leading-relaxed">
              <span className="shrink-0 w-6 h-6 rounded-full bg-plum/10 text-plum text-[12px] font-bold
                             flex items-center justify-center">{i + 1}</span>
              <span>{step}</span>
            </li>
          ))}
        </ol>
      </div>
    </div>,
    document.body,
  );
}

// ── Toggle Switch ───────────────────────────────────────────────────────────

function Toggle({ checked, onChange, disabled }: { checked: boolean; onChange: (v: boolean) => void; disabled?: boolean }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`relative shrink-0 w-11 h-6 rounded-full transition-colors duration-200
        ${checked ? 'bg-plum' : 'bg-stone'} ${disabled ? 'opacity-50' : ''}`}
    >
      <span
        className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform duration-200
          ${checked ? 'translate-x-5' : 'translate-x-0'}`}
      />
    </button>
  );
}

// ── Method Card ─────────────────────────────────────────────────────────────

interface MethodCardProps {
  def: MethodDef;
  method: OrgPaymentMethodConfig;
  isPreferred: boolean;
  onToggle: (enabled: boolean) => void;
  onSetPreferred: () => void;
  onSave: (m: OrgPaymentMethodConfig) => Promise<void>;
}

function MethodCard({ def, method, isPreferred, onToggle, onSetPreferred, onSave }: MethodCardProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(method);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showHelp, setShowHelp] = useState(false);
  // Track when we entered edit mode via toggle-on so cancel reverts the toggle
  const [pendingEnable, setPendingEnable] = useState(false);

  // Keep draft in sync when not editing
  useEffect(() => {
    if (!editing) setDraft(method);
  }, [method, editing]);

  function handleEdit() {
    setDraft(method);
    setError('');
    setPendingEnable(false);
    setEditing(true);
  }

  function handleCancel() {
    if (pendingEnable) {
      // User toggled on but cancelled before saving — revert the toggle
      onToggle(false);
      setPendingEnable(false);
    }
    setDraft(method);
    setError('');
    setEditing(false);
  }

  /** Called by the toggle. When enabling a method that needs an identifier, enter edit mode first. */
  function handleToggleInternal(enabled: boolean) {
    if (enabled && def.hasIdentifier && !method.identifier?.trim()) {
      // Optimistically show as enabled + open edit mode so user can fill in details
      setDraft({ ...method, enabled: true });
      setError('');
      setPendingEnable(true);
      setEditing(true);
      // Don't call onToggle yet — it will be saved when the user clicks Save
    } else {
      setPendingEnable(false);
      onToggle(enabled);
    }
  }

  async function handleSave() {
    // Validate
    if (def.hasIdentifier && !draft.identifier?.trim()) {
      setError(`${def.identifierLabel} is required.`);
      return;
    }
    if (def.type === 'custom' && (!draft.label?.trim() || draft.label === 'Custom')) {
      setError('A custom label is required.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      // If this save is from a toggle-on, ensure the saved draft has enabled=true
      const toSave = pendingEnable ? { ...draft, enabled: true } : draft;
      await onSave(toSave);
      setPendingEnable(false);
      setEditing(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  }

  const showHelpLink = HELP_METHODS.includes(def.type);

  return (
    <>
      <div className={`bg-white border rounded-xl shadow-brand-sm overflow-hidden transition-colors duration-150
        ${method.enabled || pendingEnable ? 'border-plum/30' : 'border-stone'}`}
      >
        {/* Header — toggle and preferred are always editable */}
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-3">
            {(['venmo', 'cashapp', 'paypal', 'zelle'] as string[]).includes(def.type) ? (
              <PaymentMethodLogo method={def.type as PaymentMethodKey} size={36} />
            ) : (
              <div className={`shrink-0 w-9 h-9 rounded-lg ${def.color} flex items-center justify-center`}>
                <span className="text-white text-[13px] font-bold">{def.icon}</span>
              </div>
            )}
            <div>
              <p className="text-[14px] font-semibold text-midnight">
                {def.hasCustomLabel && method.label !== def.label ? method.label : def.label}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {(method.enabled || pendingEnable) && !editing && def.hasIdentifier && (
              <button
                type="button"
                onClick={handleEdit}
                className="shrink-0 w-8 h-8 rounded-full flex items-center justify-center
                           bg-mist text-slate hover:text-plum transition-colors"
                title="Edit"
              >
                <Pencil size={14} />
              </button>
            )}
            {(method.enabled || pendingEnable) && (
              <button
                type="button"
                onClick={onSetPreferred}
                title={isPreferred ? 'Preferred method' : 'Set as preferred'}
                className={`shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-colors
                  ${isPreferred ? 'bg-amber-100 text-amber-600' : 'bg-mist text-slate hover:text-amber-500'}`}
              >
                <Star size={16} fill={isPreferred ? 'currentColor' : 'none'} />
              </button>
            )}
            <Toggle checked={method.enabled || pendingEnable} onChange={handleToggleInternal} />
          </div>
        </div>

        {/* Identifier — view or edit mode */}
        {(method.enabled || pendingEnable) && def.hasIdentifier && (
          <div className="px-4 pb-4 flex flex-col gap-3">
            {editing ? (
              <>
                {def.hasCustomLabel && (
                  <div>
                    <label className="block text-[11px] font-bold uppercase tracking-widest text-slate mb-1">
                      Label
                    </label>
                    <input
                      type="text"
                      value={draft.label === def.label ? '' : draft.label}
                      onChange={e => setDraft(prev => ({ ...prev, label: e.target.value.trim() || def.label }))}
                      placeholder="e.g. Wire Transfer"
                      className="w-full h-11 px-3 rounded-lg border border-stone bg-white
                                 text-small text-midnight placeholder:text-slate
                                 focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20
                                 transition-colors duration-150"
                    />
                  </div>
                )}
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-widest text-slate mb-1">
                    {def.identifierLabel}
                  </label>
                  {def.hasCustomInstructions ? (
                    <textarea
                      value={draft.identifier ?? ''}
                      onChange={e => setDraft(prev => ({ ...prev, identifier: e.target.value }))}
                      placeholder={def.identifierPlaceholder}
                      rows={3}
                      className="w-full px-3 py-2.5 rounded-lg border border-stone bg-white
                                 text-small text-midnight placeholder:text-slate resize-none
                                 focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20
                                 transition-colors duration-150"
                    />
                  ) : (
                    <input
                      type="text"
                      value={draft.identifier ?? ''}
                      onChange={e => setDraft(prev => ({ ...prev, identifier: e.target.value }))}
                      placeholder={def.identifierPlaceholder}
                      className="w-full h-11 px-3 rounded-lg border border-stone bg-white
                                 text-small text-midnight placeholder:text-slate
                                 focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20
                                 transition-colors duration-150"
                    />
                  )}
                  {showHelpLink && (
                    <button type="button" onClick={() => setShowHelp(true)}
                      className="flex items-center gap-1 mt-1.5 text-[12px] text-plum hover:text-plum/80 transition-colors">
                      <HelpCircle size={12} />
                      Where do I find this?
                    </button>
                  )}
                </div>

                {error && (
                  <div className="px-3 py-2 rounded-lg bg-rose/10 border border-rose/30 text-[13px] text-rose">
                    {error}
                  </div>
                )}

                <div className="flex gap-2">
                  <button type="button" onClick={handleCancel}
                    className="flex-1 h-10 rounded-lg border border-stone text-[13px] font-semibold text-midnight
                               hover:bg-mist transition-colors">
                    Cancel
                  </button>
                  <button type="button" onClick={handleSave} disabled={saving}
                    className="flex-1 h-10 rounded-lg bg-plum text-white text-[13px] font-semibold
                               hover:bg-rose active:opacity-80 disabled:opacity-50
                               transition-colors flex items-center justify-center gap-1.5">
                    {saving ? <><Loader2 size={14} className="animate-spin" /> Saving...</> : <><Save size={14} /> Save</>}
                  </button>
                </div>
              </>
            ) : (
              /* View mode */
              <div>
                {def.hasCustomLabel && method.label !== def.label && (
                  <p className="text-[11px] font-bold uppercase tracking-widest text-slate mb-1">Label</p>
                )}
                {def.hasCustomLabel && method.label !== def.label && (
                  <p className="text-[13px] text-midnight mb-2">{method.label}</p>
                )}
                <p className="text-[11px] font-bold uppercase tracking-widest text-slate mb-1">
                  {def.identifierLabel}
                </p>
                <p className="text-[13px] text-midnight">
                  {method.identifier?.trim() || <span className="text-slate italic">Not set</span>}
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {showHelp && <HelpModal type={def.type} onClose={() => setShowHelp(false)} />}
    </>
  );
}

// ── Stripe Connect Section ──────────────────────────────────────────────────

interface ConnectStatusData {
  status: StripeConnectStatus;
  accountId?: string;
  chargesEnabled?: boolean;
  payoutsEnabled?: boolean;
  detailsSubmitted?: boolean;
  onboardingCompletedAt?: string;
  requiresAction?: boolean;
  actionRequired?: string | null;
}

function StripeConnectSection({ orgId, stripeEnabled }: { orgId: string; stripeEnabled: boolean }) {
  const [status, setStatus] = useState<ConnectStatusData | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState('');

  async function fetchStatus() {
    setLoading(true);
    setError('');
    try {
      const res = await api.get<{ success: boolean; data: ConnectStatusData }>(`/connect/${orgId}/status`);
      setStatus(res.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load Stripe Connect status');
    } finally {
      setLoading(false);
    }
  }

  useState(() => { if (stripeEnabled || isDevMode) fetchStatus(); });

  async function handleStartOnboarding() {
    setActionLoading(true);
    setError('');
    try {
      const res = await api.post<{ success: boolean; data: { accountId: string; onboardingUrl: string } }>(
        `/connect/${orgId}/account`,
      );
      window.location.href = res.data.onboardingUrl;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start onboarding');
      setActionLoading(false);
    }
  }

  async function handleResumeOnboarding() {
    setActionLoading(true);
    setError('');
    try {
      const res = await api.post<{ success: boolean; data: { url: string } }>(
        `/connect/${orgId}/account-link`,
      );
      window.location.href = res.data.url;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate onboarding link');
      setActionLoading(false);
    }
  }

  async function handleOpenDashboard() {
    setActionLoading(true);
    setError('');
    try {
      const res = await api.post<{ success: boolean; data: { dashboardUrl: string } }>(
        `/connect/${orgId}/dashboard-link`,
      );
      window.open(res.data.dashboardUrl, '_blank');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to open dashboard');
    } finally {
      setActionLoading(false);
    }
  }

  if (!stripeEnabled && !isDevMode) {
    return null;
  }

  const statusConfig: Record<StripeConnectStatus, { label: string; color: string; icon: React.ReactNode }> = {
    not_started: { label: 'Not Connected', color: 'text-slate', icon: <CreditCard size={14} className="text-slate" /> },
    onboarding: { label: 'Onboarding', color: 'text-amber-600', icon: <Clock size={14} className="text-amber-600" /> },
    pending_verification: { label: 'Pending Verification', color: 'text-amber-600', icon: <Clock size={14} className="text-amber-600" /> },
    active: { label: 'Active', color: 'text-emerald-600', icon: <CheckCircle2 size={14} className="text-emerald-600" /> },
    restricted: { label: 'Restricted', color: 'text-rose', icon: <AlertTriangle size={14} className="text-rose" /> },
    disabled: { label: 'Disabled', color: 'text-slate', icon: <XCircle size={14} className="text-slate" /> },
  };

  const currentStatus = status?.status ?? 'not_started';
  const config = statusConfig[currentStatus];

  return (
    <div className="bg-white border border-stone rounded-xl shadow-brand-sm overflow-hidden">
      <div className="px-4 py-3 bg-mist border-b border-stone flex items-center justify-between">
        <div className="flex items-center gap-2">
          <CreditCard size={16} className="text-plum" />
          <span className="text-[11px] font-bold uppercase tracking-widest text-slate">
            Stripe Connect (Credit/Debit Cards)
          </span>
        </div>
        {!loading && status && currentStatus !== 'not_started' && (
          <button
            type="button"
            onClick={fetchStatus}
            className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[12px] font-semibold
                       text-slate hover:text-midnight hover:bg-white/60 transition-colors"
          >
            <RefreshCw size={12} />
            Refresh
          </button>
        )}
      </div>

      {error && (
        <div className="mx-4 mt-3 px-3 py-2 rounded-lg bg-rose/10 border border-rose/30 text-[13px] text-rose">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-8">
          <LoadingSpinner size="md" />
        </div>
      ) : (
        <div className="px-4 py-4">
          <div className="flex items-center gap-2 mb-3">
            {config.icon}
            <span className={`text-[13px] font-semibold ${config.color}`}>{config.label}</span>
          </div>

          {currentStatus === 'not_started' && (
            <div>
              <p className="text-[13px] text-slate leading-relaxed mb-4">
                Connect a Stripe account to accept credit and debit card payments directly from your customers.
                Stripe handles payouts, tax forms, and compliance.
              </p>
              <button
                type="button"
                onClick={handleStartOnboarding}
                disabled={actionLoading}
                className="w-full h-11 rounded-lg bg-plum text-white text-[14px] font-semibold
                           hover:bg-rose active:opacity-80 disabled:opacity-50
                           transition-colors duration-150 flex items-center justify-center gap-2"
              >
                {actionLoading ? <Loader2 size={16} className="animate-spin" /> : <CreditCard size={16} />}
                {actionLoading ? 'Setting up...' : 'Connect with Stripe'}
              </button>
            </div>
          )}

          {(currentStatus === 'onboarding' || currentStatus === 'pending_verification') && (
            <div>
              <p className="text-[13px] text-slate leading-relaxed mb-4">
                {currentStatus === 'onboarding'
                  ? 'Your Stripe account setup is incomplete. Continue onboarding to start accepting payments.'
                  : 'Your identity verification is being reviewed by Stripe. This usually takes 1-2 business days.'}
              </p>
              {currentStatus === 'onboarding' && (
                <button
                  type="button"
                  onClick={handleResumeOnboarding}
                  disabled={actionLoading}
                  className="w-full h-11 rounded-lg bg-plum text-white text-[14px] font-semibold
                             hover:bg-rose active:opacity-80 disabled:opacity-50
                             transition-colors duration-150 flex items-center justify-center gap-2"
                >
                  {actionLoading ? <Loader2 size={16} className="animate-spin" /> : <ExternalLink size={16} />}
                  {actionLoading ? 'Loading...' : 'Continue Setup'}
                </button>
              )}
            </div>
          )}

          {currentStatus === 'active' && (
            <div>
              <div className="space-y-2 mb-4">
                <div className="flex items-center justify-between">
                  <span className="text-[12px] text-slate">Charges</span>
                  <span className="text-[12px] font-semibold text-emerald-600">
                    {status?.chargesEnabled ? 'Enabled' : 'Disabled'}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-[12px] text-slate">Payouts</span>
                  <span className="text-[12px] font-semibold text-emerald-600">
                    {status?.payoutsEnabled ? 'Enabled' : 'Disabled'}
                  </span>
                </div>
                {status?.onboardingCompletedAt && (
                  <div className="flex items-center justify-between">
                    <span className="text-[12px] text-slate">Connected</span>
                    <span className="text-[12px] font-semibold text-midnight">
                      {new Date(status.onboardingCompletedAt).toLocaleDateString()}
                    </span>
                  </div>
                )}
              </div>
              <button
                type="button"
                onClick={handleOpenDashboard}
                disabled={actionLoading}
                className="w-full h-11 rounded-lg border border-stone text-[14px] font-semibold text-midnight
                           hover:border-midnight active:bg-mist disabled:opacity-50
                           transition-colors duration-150 flex items-center justify-center gap-2"
              >
                {actionLoading ? <Loader2 size={16} className="animate-spin" /> : <ExternalLink size={16} />}
                {actionLoading ? 'Loading...' : 'Open Stripe Dashboard'}
              </button>
            </div>
          )}

          {currentStatus === 'restricted' && (
            <div>
              <div className="px-3 py-2 rounded-lg bg-rose/10 border border-rose/30 mb-4">
                <p className="text-[13px] text-rose leading-relaxed">
                  Your Stripe account has been restricted.
                  {status?.requiresAction && status.actionRequired && (
                    <> Action needed: <strong>{status.actionRequired.replace(/_/g, ' ')}</strong>.</>
                  )}
                </p>
              </div>
              <button
                type="button"
                onClick={handleResumeOnboarding}
                disabled={actionLoading}
                className="w-full h-11 rounded-lg bg-amber-500 text-white text-[14px] font-semibold
                           hover:bg-amber-600 active:opacity-80 disabled:opacity-50
                           transition-colors duration-150 flex items-center justify-center gap-2"
              >
                {actionLoading ? <Loader2 size={16} className="animate-spin" /> : <AlertTriangle size={16} />}
                {actionLoading ? 'Loading...' : 'Resolve Issues'}
              </button>
            </div>
          )}

          {currentStatus === 'disabled' && (
            <p className="text-[13px] text-slate leading-relaxed">
              This Stripe Connect account has been disabled. Contact IOMI support for assistance.
            </p>
          )}

          {currentStatus === 'active' && status?.requiresAction && (
            <div className="mt-3 px-3 py-2 rounded-lg bg-amber-50 border border-amber-200">
              <p className="text-[12px] text-amber-700 leading-relaxed">
                <AlertTriangle size={12} className="inline mr-1 -mt-0.5" />
                Stripe requires additional information.
                {status.actionRequired && <> Action: <strong>{status.actionRequired.replace(/_/g, ' ')}</strong>.</>}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Payment Schedule Card ───────────────────────────────────────────────────

const DEFAULT_SCHEDULE: PaymentScheduleDefaults = {
  collectDeposit: true,
  depositType: 'percent',
  depositAmount: 50,
  balanceCollection: 'on_completion',
};

function PaymentScheduleCard({ orgId }: { orgId: string }) {
  const { defaults, loading, save } = usePaymentScheduleDefaults(orgId);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<PaymentScheduleDefaults>(DEFAULT_SCHEDULE);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Sync loaded defaults into form
  useEffect(() => {
    if (defaults) {
      setForm(defaults);
    }
  }, [defaults]);

  const isFullDeposit = form.collectDeposit && form.depositType === 'percent' && form.depositAmount === 100;

  function handleEdit() {
    setEditing(true);
    setSaveError('');
    setSaveSuccess(false);
  }

  function handleCancel() {
    setForm(defaults ?? DEFAULT_SCHEDULE);
    setEditing(false);
    setSaveError('');
  }

  async function handleSave() {
    // Validation
    if (form.collectDeposit) {
      if (form.depositAmount === undefined || form.depositAmount === null) {
        setSaveError('Please enter a deposit amount.');
        return;
      }
      if (form.depositAmount <= 0) {
        setSaveError('Deposit amount must be greater than zero.');
        return;
      }
      if (form.depositType === 'percent' && form.depositAmount > 100) {
        setSaveError('Deposit percentage cannot exceed 100%.');
        return;
      }
      if (!isFullDeposit && !form.balanceCollection) {
        setSaveError('Please choose how to collect the remaining balance.');
        return;
      }
      if (form.balanceCollection === 'split') {
        if (!form.splitBeforeMovePercent || form.splitBeforeMovePercent < 1 || form.splitBeforeMovePercent > 99) {
          setSaveError('Split percentage must be between 1% and 99%.');
          return;
        }
      }
    }

    setSaving(true);
    setSaveError('');
    try {
      await save(form);
      setEditing(false);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  }

  // Compute the resolved schedule type label for display
  function getScheduleSummary(): string {
    if (!form.collectDeposit) return 'Full payment at completion';
    if (isFullDeposit) return '100% deposit at booking';
    const depLabel = form.depositType === 'fixed'
      ? `$${form.depositAmount ?? 0}`
      : `${form.depositAmount ?? 0}%`;
    const balLabel =
      form.balanceCollection === 'before_move' ? 'balance before move' :
      form.balanceCollection === 'on_completion' ? 'balance at completion' :
      form.balanceCollection === 'split'
        ? `balance split ${form.splitBeforeMovePercent}% before / ${100 - (form.splitBeforeMovePercent ?? 50)}% after`
        : 'balance collection not set';
    return `${depLabel} deposit, ${balLabel}`;
  }

  function getDepositLabel(): string {
    if (!form.collectDeposit) return 'No';
    if (isFullDeposit) return 'Yes — 100% upfront';
    if (form.depositType === 'fixed') return `Yes — $${form.depositAmount ?? 0} fixed`;
    return `Yes — ${form.depositAmount ?? 0}% of quote`;
  }

  function getBalanceLabel(): string {
    if (!form.collectDeposit) return 'Full amount due at completion';
    if (isFullDeposit) return 'N/A — full deposit collected at booking';
    switch (form.balanceCollection) {
      case 'before_move': return 'One payment before the move';
      case 'on_completion': return 'One payment after the move';
      case 'split': {
        const before = form.splitBeforeMovePercent ?? 50;
        return `Split — ${before}% before move, ${100 - before}% after`;
      }
      default: return 'Not configured';
    }
  }

  if (loading) {
    return (
      <div className="bg-white border border-stone rounded-xl shadow-brand-sm overflow-hidden">
        <div className="px-4 py-3.5 border-b border-stone">
          <p className="font-semibold text-small text-midnight">Payment Schedule</p>
        </div>
        <div className="flex justify-center py-8">
          <LoadingSpinner size="md" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white border border-stone rounded-xl shadow-brand-sm overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3.5 border-b border-stone flex items-center justify-between">
        <div>
          <p className="font-semibold text-small text-midnight">Payment Schedule</p>
          <p className="text-[12px] text-slate mt-0.5 leading-snug">
            Default payment schedule for booked jobs.
          </p>
        </div>
        {!editing && (
          <button
            type="button"
            onClick={handleEdit}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-semibold
                       text-plum hover:bg-plum/10 transition-colors"
          >
            <Pencil size={12} />
            Edit
          </button>
        )}
      </div>

      <div className="px-4 py-4 space-y-4">
        {/* — View mode: expanded details — */}
        {!editing && !defaults && (
          <div className="flex items-center gap-3 py-2">
            <div className="shrink-0 w-9 h-9 rounded-lg bg-mist flex items-center justify-center">
              <AlertTriangle size={16} className="text-slate" />
            </div>
            <div>
              <p className="text-[14px] font-semibold text-midnight">Not configured</p>
              <p className="text-[12px] text-slate mt-0.5">
                Set up how you want to collect payments for booked jobs.
              </p>
            </div>
          </div>
        )}

        {!editing && defaults && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
            {/* Deposit */}
            <div>
              <p className="text-[11px] font-bold uppercase tracking-widest text-slate mb-1">
                Collect Deposit
              </p>
              <p className="text-[14px] font-semibold text-midnight">{getDepositLabel()}</p>
            </div>

            {/* Balance Collection */}
            <div>
              <p className="text-[11px] font-bold uppercase tracking-widest text-slate mb-1">
                Balance Collection
              </p>
              <p className="text-[14px] font-semibold text-midnight">{getBalanceLabel()}</p>
            </div>

            {/* Summary */}
            <div>
              <p className="text-[11px] font-bold uppercase tracking-widest text-slate mb-1">
                Summary
              </p>
              <p className="text-[14px] font-semibold text-midnight">{getScheduleSummary()}</p>
            </div>
          </div>
        )}

        {saveSuccess && !editing && (
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-50 border border-emerald-200">
            <CheckCircle2 size={14} className="text-emerald-600" />
            <span className="text-[13px] text-emerald-700 font-medium">Payment schedule saved</span>
          </div>
        )}

        {/* — Edit mode — */}
        {editing && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {/* Collect deposit? */}
              <div>
                <p className="text-[11px] font-bold uppercase tracking-widest text-slate mb-2">
                  Collect Deposit
                </p>
                <div className="flex gap-2 max-w-[240px]">
                  <button
                    type="button"
                    onClick={() => setForm(prev => ({ ...prev, collectDeposit: true }))}
                    className={`flex-1 h-11 rounded-lg text-[14px] font-semibold border transition-colors
                      ${form.collectDeposit
                        ? 'bg-plum text-white border-plum'
                        : 'bg-white text-midnight border-stone hover:border-plum/40'}`}
                  >
                    Yes
                  </button>
                  <button
                    type="button"
                    onClick={() => setForm(prev => ({
                      ...prev,
                      collectDeposit: false,
                      depositAmount: undefined,
                      balanceCollection: undefined,
                      splitBeforeMovePercent: undefined,
                    }))}
                    className={`flex-1 h-11 rounded-lg text-[14px] font-semibold border transition-colors
                      ${!form.collectDeposit
                        ? 'bg-plum text-white border-plum'
                        : 'bg-white text-midnight border-stone hover:border-plum/40'}`}
                  >
                    No
                  </button>
                </div>
                {!form.collectDeposit && (
                  <p className="text-[12px] text-slate mt-2 leading-snug">
                    The full quote amount will be due when the job is complete.
                  </p>
                )}
              </div>

              {/* Deposit amount — only when collecting deposit */}
              {form.collectDeposit && (
                <div>
                  <p className="text-[11px] font-bold uppercase tracking-widest text-slate mb-2">
                    Deposit Amount
                  </p>

                  {/* Fixed / Percent toggle */}
                  <div className="flex rounded-lg border border-stone overflow-hidden mb-3 max-w-[260px]">
                    <button
                      type="button"
                      onClick={() => setForm(prev => ({ ...prev, depositType: 'fixed' }))}
                      className={`flex-1 py-2 text-[13px] font-medium transition-colors
                        ${form.depositType === 'fixed'
                          ? 'bg-plum text-white'
                          : 'bg-white text-slate hover:bg-mist'}`}
                    >
                      Fixed Amount
                    </button>
                    <button
                      type="button"
                      onClick={() => setForm(prev => ({ ...prev, depositType: 'percent' }))}
                      className={`flex-1 py-2 text-[13px] font-medium border-l border-stone transition-colors
                        ${form.depositType === 'percent'
                          ? 'bg-plum text-white'
                          : 'bg-white text-slate hover:bg-mist'}`}
                    >
                      % of Quote
                    </button>
                  </div>

                  {/* Amount input */}
                  <div className="relative max-w-[200px]">
                    {form.depositType === 'fixed' ? (
                      <>
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate text-[14px]">$</span>
                        <input
                          type="number"
                          min={0}
                          step={1}
                          value={form.depositAmount ?? ''}
                          onChange={e => {
                            const val = e.target.value;
                            setForm(prev => ({
                              ...prev,
                              depositAmount: val === '' ? undefined : Number(val),
                            }));
                          }}
                          className="w-full h-11 pl-7 pr-3 rounded-lg border border-stone bg-white
                                     text-small text-midnight placeholder:text-slate
                                     focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20
                                     transition-colors duration-150"
                          placeholder="0"
                        />
                      </>
                    ) : (
                      <>
                        <input
                          type="number"
                          min={0}
                          max={100}
                          step={1}
                          value={form.depositAmount ?? ''}
                          onChange={e => {
                            const val = e.target.value;
                            setForm(prev => ({
                              ...prev,
                              depositAmount: val === '' ? undefined : Math.min(100, Number(val)),
                            }));
                          }}
                          className="w-full h-11 pl-3 pr-8 rounded-lg border border-stone bg-white
                                     text-small text-midnight placeholder:text-slate
                                     focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20
                                     transition-colors duration-150"
                          placeholder="0"
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate text-[14px]">%</span>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Balance collection (when deposit is < 100%) */}
            {form.collectDeposit && !isFullDeposit && (
              <div className="pt-2 border-t border-stone">
                <p className="text-[11px] font-bold uppercase tracking-widest text-slate mb-2">
                  Balance Collection
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                  {([
                    { value: 'before_move' as const, label: 'Before the move', desc: 'Customer pays the balance before the move starts.' },
                    { value: 'on_completion' as const, label: 'After the move', desc: 'Customer pays the balance when the job is complete.' },
                    { value: 'split' as const, label: 'Split before & after', desc: 'Balance is split into two payments.' },
                  ]).map(opt => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setForm(prev => ({
                        ...prev,
                        balanceCollection: opt.value,
                        ...(opt.value !== 'split' ? { splitBeforeMovePercent: undefined } : {}),
                        ...(opt.value === 'split' && !prev.splitBeforeMovePercent ? { splitBeforeMovePercent: 50 } : {}),
                      }))}
                      className={`text-left px-4 py-3 rounded-lg border transition-colors
                        ${form.balanceCollection === opt.value
                          ? 'border-plum bg-plum/5'
                          : 'border-stone bg-white hover:border-plum/40'}`}
                    >
                      <p className={`text-[14px] font-semibold ${form.balanceCollection === opt.value ? 'text-plum' : 'text-midnight'}`}>
                        {opt.label}
                      </p>
                      <p className="text-[12px] text-slate mt-0.5">{opt.desc}</p>
                    </button>
                  ))}
                </div>

                {/* Split percentage */}
                {form.balanceCollection === 'split' && (
                  <div className="mt-3 p-3 bg-mist rounded-lg max-w-sm">
                    <p className="text-[13px] font-semibold text-midnight mb-2">
                      Split percentage
                    </p>
                    <div className="flex items-center gap-3">
                      <div className="flex-1">
                        <label className="block text-[11px] font-bold uppercase tracking-widest text-slate mb-1">
                          Before move
                        </label>
                        <div className="relative">
                          <input
                            type="number"
                            min={1}
                            max={99}
                            step={1}
                            value={form.splitBeforeMovePercent ?? 50}
                            onChange={e => {
                              const val = Math.min(99, Math.max(1, Number(e.target.value)));
                              setForm(prev => ({ ...prev, splitBeforeMovePercent: val }));
                            }}
                            className="w-full h-11 pl-3 pr-8 rounded-lg border border-stone bg-white
                                       text-small text-midnight
                                       focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20
                                       transition-colors duration-150"
                          />
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate text-[14px]">%</span>
                        </div>
                      </div>
                      <div className="flex-1">
                        <label className="block text-[11px] font-bold uppercase tracking-widest text-slate mb-1">
                          After move
                        </label>
                        <div className="h-11 px-3 rounded-lg border border-stone bg-mist
                                        flex items-center text-small text-slate">
                          {100 - (form.splitBeforeMovePercent ?? 50)}%
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Error */}
            {saveError && (
              <div className="px-3 py-2 rounded-lg bg-rose/10 border border-rose/30 text-[13px] text-rose">
                {saveError}
              </div>
            )}

            {/* Save / Cancel buttons */}
            <div className="flex gap-2 pt-1 max-w-xs">
              <button
                type="button"
                onClick={handleCancel}
                className="flex-1 h-11 rounded-lg border border-stone text-[14px] font-semibold text-midnight
                           hover:bg-mist transition-colors duration-150"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="flex-1 h-11 rounded-lg bg-plum text-white text-[14px] font-semibold
                           hover:bg-rose active:opacity-80 disabled:opacity-50
                           transition-colors duration-150 flex items-center justify-center gap-2"
              >
                {saving ? (
                  <><Loader2 size={16} className="animate-spin" /> Saving...</>
                ) : (
                  <><Save size={16} /> Save</>
                )}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

export function CustomerPaymentsPage({ view }: { view?: 'methods' | 'schedule' } = {}) {
  const navigate = useNavigate();
  const { adminOrg, loading: profileLoading } = useUserProfile();
  const orgId = adminOrg?.orgId ?? '';
  const { config, loading, error, save } = usePaymentConfig(orgId);
  const { org: orgSettings, updateSettings: updateOrgSettings } = useOrgSettings(orgId);

  const stripeConnectAllowed = orgSettings?.allowedFeatureStripeConnect ?? false;
  const stripeConnectEnabled = orgSettings?.featureStripeConnect ?? false;

  // Local form state
  const [methods, setMethods] = useState<OrgPaymentMethodConfig[]>(getDefaultMethods());
  const [customMessage, setCustomMessage] = useState('');
  const [customMessageEditing, setCustomMessageEditing] = useState(false);
  const [customMessageSaving, setCustomMessageSaving] = useState(false);
  const [customMessageSuccess, setCustomMessageSuccess] = useState(false);

  // Sync from loaded config
  useEffect(() => {
    if (config) {
      // Merge loaded methods with defaults to ensure all types are present
      const merged = METHOD_DEFS.map(def => {
        const existing = config.methods.find(m => m.type === def.type);
        return existing ?? { type: def.type, enabled: false, label: def.label, identifier: '' };
      });
      setMethods(merged);
      setCustomMessage(config.customMessage ?? '');
    }
  }, [config]);

  // Save the full config (used when toggling enable/disable or preferred, and per-card save)
  const saveConfig = useCallback(async (updatedMethods: OrgPaymentMethodConfig[], msg?: string) => {
    const payload: OrgPaymentConfig = {
      methods: updatedMethods,
      ...(msg?.trim() ? { customMessage: msg.trim() } : customMessage?.trim() ? { customMessage: customMessage.trim() } : {}),
    };
    await save(payload);
  }, [save, customMessage]);

  // Handle toggle (enable/disable) — auto-saves immediately
  async function handleToggle(idx: number, enabled: boolean) {
    const updated = methods.map((m, i) => i === idx ? { ...m, enabled } : m);
    setMethods(updated);
    try {
      await saveConfig(updated);
    } catch {
      // Revert on error
      setMethods(methods);
    }
  }

  // Handle preferred — auto-saves immediately
  async function handleSetPreferred(idx: number) {
    const updated = methods.map((m, i) => ({
      ...m,
      preferred: i === idx ? !m.preferred : false,
    }));
    setMethods(updated);
    try {
      await saveConfig(updated);
    } catch {
      setMethods(methods);
    }
  }

  // Per-card save: saves the updated method config
  async function handleMethodSave(idx: number, updatedMethod: OrgPaymentMethodConfig) {
    const updated = methods.map((m, i) => i === idx ? updatedMethod : m);
    setMethods(updated);
    await saveConfig(updated);
  }

  // Custom message save
  async function handleCustomMessageSave() {
    setCustomMessageSaving(true);
    try {
      await saveConfig(methods, customMessage);
      setCustomMessageEditing(false);
      setCustomMessageSuccess(true);
      setTimeout(() => setCustomMessageSuccess(false), 3000);
    } catch {
      // keep editing open
    } finally {
      setCustomMessageSaving(false);
    }
  }

  if (profileLoading || !orgId) {
    return (
      <div className="px-4 pt-6 pb-8 md:px-8 md:max-w-3xl lg:max-w-5xl md:mx-auto">
        <div className="flex justify-center py-16">
          <LoadingSpinner size="lg" />
        </div>
      </div>
    );
  }

  const hasEnabledOfflineMethod = methods.some(m => m.enabled);

  return (
    <div className="px-4 pt-6 pb-12 md:px-8 md:max-w-2xl lg:max-w-4xl md:mx-auto">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-1 text-small mb-4 -ml-1">
        <button
          type="button"
          onClick={() => navigate('/admin')}
          className="text-slate hover:text-midnight transition-colors duration-150"
        >
          Admin Panel
        </button>
        <ChevronRight size={14} className="text-stone" />
        <button
          type="button"
          onClick={() => navigate('/admin/payments')}
          className="text-slate hover:text-midnight transition-colors duration-150"
        >
          Payments
        </button>
        <ChevronRight size={14} className="text-stone" />
        <span className="text-midnight font-medium">
          {view === 'methods' ? 'Payment Methods' : view === 'schedule' ? 'Payment Schedule' : 'Customer Payments'}
        </span>
      </nav>

      <h1 className="font-display text-h1 text-midnight mb-1">
        {view === 'methods' ? 'Payment Methods' : view === 'schedule' ? 'Payment Schedule' : 'Customer Payments'}
      </h1>
      <p className="text-small text-slate mb-6">
        {view === 'methods'
          ? 'Stripe Connect, offline methods, and custom messages.'
          : view === 'schedule'
            ? 'Default deposit, balance split, and lock-in moving date.'
            : 'Configure which payment methods your customers can use.'}
      </p>

      {loading ? (
        <div className="flex justify-center py-16">
          <LoadingSpinner size="lg" />
        </div>
      ) : error ? (
        <div className="px-4 py-3 rounded-lg bg-rose/10 border border-rose/30 text-small text-rose">
          {error}
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          {/* Payment Schedule */}
          {(!view || view === 'schedule') && (
            <PaymentScheduleCard orgId={orgId} />
          )}

          {/* Calculate Taxes toggle */}
          {(!view || view === 'methods') && orgSettings && (
            <CalculateTaxesCard org={orgSettings} onSave={updateOrgSettings} />
          )}

          {/* Stripe Connect */}
          {(!view || view === 'methods') && (stripeConnectAllowed || isDevMode) && (
            <StripeConnectSection orgId={orgId} stripeEnabled={stripeConnectEnabled} />
          )}

          {/* Offline Payment Methods */}
          {(!view || view === 'methods') && (
            <>
              {/* Warning callout when any offline method is enabled */}
              {hasEnabledOfflineMethod && (
                <div className="flex items-start gap-3 px-4 py-3 rounded-xl bg-amber-50 border border-amber-200">
                  <Info size={16} className="shrink-0 text-amber-600 mt-0.5" />
                  <p className="text-[13px] text-amber-800 leading-relaxed">
                    Payments received through offline methods (cash, check, Venmo, etc.) must be
                    logged manually on the Job&apos;s page to maintain accurate payment tracking.
                  </p>
                </div>
              )}

              {methods.map((method, idx) => {
                const def = METHOD_DEFS.find(d => d.type === method.type)!;
                return (
                  <MethodCard
                    key={method.type}
                    def={def}
                    method={method}
                    isPreferred={method.preferred ?? false}
                    onToggle={(enabled) => handleToggle(idx, enabled)}
                    onSetPreferred={() => handleSetPreferred(idx)}
                    onSave={(m) => handleMethodSave(idx, m)}
                  />
                );
              })}

              {/* Custom message */}
              <div className="bg-white border border-stone rounded-xl shadow-brand-sm overflow-hidden">
                <div className="px-4 py-3 bg-mist border-b border-stone flex items-center justify-between">
                  <div>
                    <span className="text-[11px] font-bold uppercase tracking-widest text-slate">
                      Custom Message
                    </span>
                    <p className="text-[12px] text-slate mt-0.5 leading-snug">
                      Optional message included in payment request communications.
                    </p>
                  </div>
                  {!customMessageEditing && (
                    <button type="button" onClick={() => { setCustomMessageEditing(true); setCustomMessageSuccess(false); }}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-semibold
                                 text-plum hover:bg-plum/10 transition-colors shrink-0">
                      <Pencil size={12} />
                      Edit
                    </button>
                  )}
                </div>
                <div className="px-4 py-4">
                  {customMessageEditing ? (
                    <div className="flex flex-col gap-3">
                      <textarea
                        value={customMessage}
                        onChange={e => setCustomMessage(e.target.value)}
                        placeholder="e.g. Thank you for choosing us! Please complete your payment within 48 hours."
                        rows={3}
                        className="w-full px-3 py-2.5 rounded-lg border border-stone bg-white
                                   text-small text-midnight placeholder:text-slate resize-none
                                   focus:outline-none focus:border-plum focus:ring-2 focus:ring-plum/20
                                   transition-colors duration-150"
                      />
                      <div className="flex gap-2">
                        <button type="button" onClick={() => { setCustomMessage(config?.customMessage ?? ''); setCustomMessageEditing(false); }}
                          className="flex-1 h-10 rounded-lg border border-stone text-[13px] font-semibold text-midnight
                                     hover:bg-mist transition-colors">
                          Cancel
                        </button>
                        <button type="button" onClick={handleCustomMessageSave} disabled={customMessageSaving}
                          className="flex-1 h-10 rounded-lg bg-plum text-white text-[13px] font-semibold
                                     hover:bg-rose active:opacity-80 disabled:opacity-50
                                     transition-colors flex items-center justify-center gap-1.5">
                          {customMessageSaving ? <><Loader2 size={14} className="animate-spin" /> Saving...</> : <><Save size={14} /> Save</>}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <p className="text-[13px] text-midnight">
                        {customMessage?.trim() || <span className="text-slate italic">No custom message set</span>}
                      </p>
                      {customMessageSuccess && (
                        <div className="flex items-center gap-2 mt-3 px-3 py-2 rounded-lg bg-emerald-50 border border-emerald-200">
                          <CheckCircle2 size={14} className="text-emerald-600" />
                          <span className="text-[13px] text-emerald-700 font-medium">Saved</span>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
